import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Alert,
  Skeleton,
  useTheme,
  Fade,
  IconButton,
  Paper,
  Modal,
  Stack,
  Divider,
  Chip,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import {
  ArrowBack,
  Close,
  Email,
  CalendarToday,
  Person,
  Pets,
  School,
  Payment,
  CheckCircle,
  Warning,
  BarChart,
  PictureAsPdf,
} from "@mui/icons-material";
import { DataGrid, GridColDef, GridRowParams } from "@mui/x-data-grid";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import Cookies from "universal-cookie";
import type { EntryOverview, FlattenedEntry } from "../component";

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "800px",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  overflow: "auto",
};

const ShowEntryOverviews = (): JSX.Element => {
  const { showId } = useParams<{ showId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [entryOverviews, setEntryOverviews] = useState<EntryOverview[]>([]);
  const [flattenedEntries, setFlattenedEntries] = useState<FlattenedEntry[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<EntryOverview | null>(
    null,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [showName, setShowName] = useState<string>("");
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingOverviewsPdf, setLoadingOverviewsPdf] = useState(false);
  const [loadingClassPdfId, setLoadingClassPdfId] = useState<number | null>(
    null,
  );

  const classNameToId = useMemo(() => {
    const map: Record<string, number> = {};
    entryOverviews.forEach((overview) => {
      overview.payload.entries.forEach((entry) => {
        entry.class_details?.forEach((cd) => {
          if (cd.class_name && cd.class_id) {
            map[cd.class_name] = cd.class_id;
          }
        });
      });
    });
    return map;
  }, [entryOverviews]);

  useEffect(() => {
    if (showId) {
      getEntryOverviews(parseInt(showId));
    }
  }, [showId]);

  const getEntryOverviews = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const token = new Cookies().get("token");

      const response = await axios.get(
        `https://api.easyagility.co.uk/shows/${id}/entry-overviews`,
        { headers: { Authorization: "Bearer " + token } },
      );

      setEntryOverviews(response.data);

      // Get show name from first entry if available
      if (response.data.length > 0) {
        setShowName(response.data[0].payload.show_name);
      }

      // Flatten the entries for the DataGrid
      const flattened: FlattenedEntry[] = [];
      response.data.forEach((overview: EntryOverview) => {
        overview.payload.entries.forEach((entry, index) => {
          // Get class names from class_details array
          const classNames = entry.class_details
            ? entry.class_details
                .map((cd: any) => cd.class_name)
                .filter((name: string) => name)
                .join(", ")
            : entry.class_name || "";

          flattened.push({
            id: `${overview.id}-${index}`,
            reference: overview.reference,
            email_address: overview.email_address,
            dog_name: entry.dog_name || "",
            handler_name:
              entry.handler_name ||
              `${entry.handler_first_name || ""} ${
                entry.handler_last_name || ""
              }`.trim(),
            grade: entry.grade,
            height: entry.height,
            class_name: classNames,
            kennel_club_name: entry.kennel_club_name || "",
            show_fees: overview.payload.show_fees,
            paid: overview.paid,
            created_at: overview.created_at,
            entryOverviewId: overview.id, // Store the overview ID for lookup
          });
        });
      });

      setFlattenedEntries(flattened);
    } catch (err) {
      setError("Failed to load entry overviews. Please try again later.");
      console.error("Error fetching entry overviews:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (params: GridRowParams) => {
    // Find the entry overview that contains this row
    const entryOverviewId = params.row.entryOverviewId;
    const overview = entryOverviews.find((eo) => eo.id === entryOverviewId);
    if (overview) {
      setSelectedEntry(overview);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEntry(null);
  };

  const pdfBlobFromBase64 = (pdf: string) => {
    const binaryString = atob(pdf);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: "application/pdf" });
  };

  const downloadPdfFromBase64 = (pdf: string, filename: string) => {
    const url = URL.createObjectURL(pdfBlobFromBase64(pdf));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleViewCompetitorsPdf = async () => {
    if (!showId) return;

    try {
      setLoadingPdf(true);
      const token = new Cookies().get("token");

      const response = await axios.get(
        `https://api.easyagility.co.uk/shows/${showId}/competitors/base64`,
        { headers: { Authorization: "Bearer " + token } },
      );

      const safeName = (showName || "show")
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-");
      downloadPdfFromBase64(
        response.data.pdf,
        `${safeName || "show"}-competitors.pdf`,
      );
    } catch (err) {
      console.error("Error fetching competitors PDF:", err);
      setError("Failed to download competitors PDF. Please try again later.");
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleDownloadClassCompetitorsPdf = async (
    classId: number,
    className: string,
  ) => {
    try {
      setLoadingClassPdfId(classId);
      setError(null);
      const token = new Cookies().get("token");

      const response = await axios.get(
        `https://api.easyagility.co.uk/classes/${classId}/competitors/base64`,
        { headers: { Authorization: "Bearer " + token } },
      );

      const safeShow = (showName || "show")
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-");
      const safeClass = className
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-");
      downloadPdfFromBase64(
        response.data.pdf,
        `${safeShow || "show"}-${safeClass || "class"}-competitors.pdf`,
      );
    } catch (err) {
      console.error("Error fetching class competitors PDF:", err);
      setError(
        `Failed to download competitors PDF for ${className}. Please try again later.`,
      );
    } finally {
      setLoadingClassPdfId(null);
    }
  };

  const handleViewEntryOverviewsPdf = async () => {
    if (!showId) return;

    try {
      setLoadingOverviewsPdf(true);
      const token = new Cookies().get("token");

      const response = await axios.get(
        `https://api.easyagility.co.uk/shows/${showId}/entry-overviews/base64`,
        { headers: { Authorization: "Bearer " + token } },
      );

      const safeName = (showName || "show")
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-");
      downloadPdfFromBase64(
        response.data.pdf,
        `${safeName || "show"}-entry-overviews.pdf`,
      );
    } catch (err) {
      console.error("Error fetching entry overviews PDF:", err);
      setError(
        "Failed to download entry overviews PDF. Please try again later.",
      );
    } finally {
      setLoadingOverviewsPdf(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount: string): string => {
    return `£${parseFloat(amount || "0").toFixed(2)}`;
  };

  // Calculate total show fees from entry overviews (each overview counted once)
  const totalShowFees = entryOverviews.reduce((sum, overview) => {
    return sum + parseFloat(overview.payload.show_fees || "0");
  }, 0);

  // Calculate paid and unpaid amounts
  const paidAmount = entryOverviews
    .filter((overview) => overview.paid)
    .reduce((sum, overview) => {
      return sum + parseFloat(overview.payload.show_fees || "0");
    }, 0);

  const unpaidAmount = entryOverviews
    .filter((overview) => !overview.paid)
    .reduce((sum, overview) => {
      return sum + parseFloat(overview.payload.show_fees || "0");
    }, 0);

  // Calculate summary statistics
  const totalEntries = flattenedEntries.length;
  const uniqueAccounts = new Set(entryOverviews.map((eo) => eo.account_id))
    .size;
  const paidEntries = entryOverviews.filter((eo) => eo.paid).length;
  const unpaidEntries = entryOverviews.filter((eo) => !eo.paid).length;

  // Calculate dogs per grade per height
  const gradeHeightBreakdown: {
    [height: string]: { [grade: number]: number };
  } = {};
  flattenedEntries.forEach((entry) => {
    const height = entry.height || "Unknown";
    const grade = entry.grade;
    if (!gradeHeightBreakdown[height]) {
      gradeHeightBreakdown[height] = {};
    }
    if (!gradeHeightBreakdown[height][grade]) {
      gradeHeightBreakdown[height][grade] = 0;
    }
    gradeHeightBreakdown[height][grade]++;
  });

  // Calculate total dogs per height
  const heightTotals: { [height: string]: number } = {};
  flattenedEntries.forEach((entry) => {
    const height = entry.height || "Unknown";
    heightTotals[height] = (heightTotals[height] || 0) + 1;
  });

  // Calculate total dogs per grade
  const gradeTotals: { [grade: number]: number } = {};
  flattenedEntries.forEach((entry) => {
    const grade = entry.grade;
    gradeTotals[grade] = (gradeTotals[grade] || 0) + 1;
  });

  // Calculate dogs over time (more granular - by hour)
  const entriesOverTime: {
    timestamp: string;
    displayTime: string;
    cumulative: number;
  }[] = [];
  const timeCounts: { [timestamp: string]: number } = {};
  let cumulative = 0;

  // Group dogs by hour for more granular view
  entryOverviews.forEach((overview) => {
    const date = new Date(overview.created_at);
    // Round to nearest hour for grouping
    date.setMinutes(0, 0, 0);
    const timestamp = date.getTime();
    const timestampKey = timestamp.toString();
    // Count the number of dogs (entries) in this overview
    const dogCount = overview.payload.entries.length;
    timeCounts[timestampKey] = (timeCounts[timestampKey] || 0) + dogCount;
  });

  // Convert to array and sort by timestamp
  const sortedTimestamps = Object.keys(timeCounts).sort(
    (a, b) => parseInt(a) - parseInt(b),
  );

  sortedTimestamps.forEach((timestampKey) => {
    const count = timeCounts[timestampKey];
    cumulative += count;
    const date = new Date(parseInt(timestampKey));

    // Format for display - show date and time
    const displayTime = date.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

    entriesOverTime.push({
      timestamp: timestampKey,
      displayTime: displayTime,
      cumulative: cumulative,
    });
  });

  // Calculate dogs per class, height, and grade
  const classHeightGradeBreakdown: {
    [className: string]: {
      [height: string]: { [grade: number]: number };
    };
  } = {};

  entryOverviews.forEach((overview) => {
    overview.payload.entries.forEach((entry) => {
      const height = entry.height || "Unknown";
      const grade = entry.grade;

      // Get classes from class_details array or fallback to class_name
      const classes = entry.class_details
        ? entry.class_details.map((cd: any) => cd.class_name).filter(Boolean)
        : entry.class_name
          ? [entry.class_name]
          : ["Unknown"];

      // Count this entry for each class it's in
      classes.forEach((className: string) => {
        if (!classHeightGradeBreakdown[className]) {
          classHeightGradeBreakdown[className] = {};
        }
        if (!classHeightGradeBreakdown[className][height]) {
          classHeightGradeBreakdown[className][height] = {};
        }
        if (!classHeightGradeBreakdown[className][height][grade]) {
          classHeightGradeBreakdown[className][height][grade] = 0;
        }
        classHeightGradeBreakdown[className][height][grade]++;
      });
    });
  });

  // Calculate totals per class
  const classTotals: { [className: string]: number } = {};
  Object.keys(classHeightGradeBreakdown).forEach((className) => {
    let total = 0;
    Object.keys(classHeightGradeBreakdown[className]).forEach((height) => {
      Object.keys(classHeightGradeBreakdown[className][height]).forEach(
        (grade) => {
          total +=
            classHeightGradeBreakdown[className][height][parseInt(grade)];
        },
      );
    });
    classTotals[className] = total;
  });

  const columns: GridColDef[] = [
    {
      field: "reference",
      headerName: "Reference",
      flex: 0.15,
      minWidth: 120,
    },
    {
      field: "email_address",
      headerName: "Email",
      flex: 0.2,
      minWidth: 180,
    },
    {
      field: "dog_name",
      headerName: "Dog Name",
      flex: 0.15,
      minWidth: 120,
    },
    {
      field: "handler_name",
      headerName: "Handler",
      flex: 0.15,
      minWidth: 150,
    },
    {
      field: "grade",
      headerName: "Grade",
      flex: 0.08,
      minWidth: 80,
      type: "number",
    },
    {
      field: "height",
      headerName: "Height",
      flex: 0.1,
      minWidth: 100,
    },
    {
      field: "class_name",
      headerName: "Class",
      flex: 0.15,
      minWidth: 120,
    },
    {
      field: "kennel_club_name",
      headerName: "Kennel Club Name",
      flex: 0.2,
      minWidth: 150,
    },
    {
      field: "show_fees",
      headerName: "Show Fees",
      flex: 0.12,
      minWidth: 100,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: "paid",
      headerName: "Paid",
      flex: 0.1,
      minWidth: 80,
      type: "boolean",
      renderCell: (params) => (
        <Chip
          label={params.value ? "Yes" : "No"}
          color={params.value ? "success" : "warning"}
          size="small"
        />
      ),
    },
    {
      field: "created_at",
      headerName: "Created",
      flex: 0.15,
      minWidth: 150,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        return date.toLocaleString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
  ];

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: 3 }}>
        <Skeleton variant="rectangular" height={400} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ py: 4, px: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, px: 3 }}>
      <Fade in timeout={600}>
        <Box>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <IconButton
                  onClick={() => navigate("/show-management")}
                  sx={{ color: theme.palette.primary.main }}
                >
                  <ArrowBack />
                </IconButton>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                  }}
                >
                  {showName || "Show"} - Entry Overviews
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdf />}
                  onClick={handleViewEntryOverviewsPdf}
                  disabled={loadingOverviewsPdf || !showId}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  {loadingOverviewsPdf
                    ? "Loading..."
                    : "Download Entry Overviews PDF"}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdf />}
                  onClick={handleViewCompetitorsPdf}
                  disabled={loadingPdf || !showId}
                  sx={{
                    backgroundColor: theme.palette.error.main,
                    "&:hover": {
                      backgroundColor: theme.palette.error.dark,
                    },
                  }}
                >
                  {loadingPdf ? "Loading..." : "Download Competitors PDF"}
                </Button>
              </Box>
            </Box>
            {entryOverviews.length > 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: theme.palette.primary.light + "10",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Total Show Fees
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(totalShowFees.toString())}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: theme.palette.success.light + "10",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Paid
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.success.main,
                      }}
                    >
                      {formatCurrency(paidAmount.toString())}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: theme.palette.warning.light + "10",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Unpaid
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.warning.main,
                      }}
                    >
                      {formatCurrency(unpaidAmount.toString())}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Entries Table */}
          {flattenedEntries.length === 0 ? (
            <Alert severity="info">No entries found for this show.</Alert>
          ) : (
            <Paper sx={{ height: "70vh", width: "100%", mb: 4 }}>
              <DataGrid
                rows={flattenedEntries}
                columns={columns}
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 100 },
                  },
                }}
                density="compact"
                disableRowSelectionOnClick
                onRowClick={handleRowClick}
                sx={{
                  width: "100%",
                  "& .MuiDataGrid-cell": {
                    fontSize: "0.875rem",
                    cursor: "pointer",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  "& .MuiDataGrid-columnHeader": {
                    fontSize: "0.875rem",
                    fontWeight: 600,
                  },
                }}
              />
            </Paper>
          )}

          {/* Summary Statistics */}
          {flattenedEntries.length > 0 && (
            <Box sx={{ mt: 4, mb: 4 }}>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <BarChart /> Summary Statistics
              </Typography>

              {/* Entries Over Time Chart */}
              {entriesOverTime.length > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    mb: 3,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 3 }}
                    gutterBottom
                  >
                    Entries Received Over Time
                  </Typography>
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart
                      data={entriesOverTime}
                      margin={{
                        top: 10,
                        right: 30,
                        left: 0,
                        bottom: 60,
                      }}
                    >
                      <defs>
                        <linearGradient
                          id="colorCumulative"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={theme.palette.primary.main}
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor={theme.palette.primary.main}
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={theme.palette.divider}
                        opacity={0.5}
                      />
                      <XAxis
                        dataKey="displayTime"
                        tick={{
                          fontSize: 11,
                          fill: theme.palette.text.secondary,
                        }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{
                          fontSize: 11,
                          fill: theme.palette.text.secondary,
                        }}
                        label={{
                          value: "Total Dogs",
                          angle: -90,
                          position: "insideLeft",
                          style: {
                            textAnchor: "middle",
                            fill: theme.palette.text.primary,
                          },
                        }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 8,
                          boxShadow: theme.shadows[4],
                          padding: "12px",
                        }}
                        labelStyle={{
                          fontWeight: 600,
                          marginBottom: 8,
                          color: theme.palette.text.primary,
                        }}
                        formatter={(value: number | undefined) => [
                          `${value ?? 0} dogs`,
                          "Cumulative Total",
                        ]}
                      />
                      <Area
                        type="monotone"
                        dataKey="cumulative"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2.5}
                        fillOpacity={1}
                        fill="url(#colorCumulative)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Paper>
              )}

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Total Entries
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {totalEntries}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {flattenedEntries.length} dog
                        {flattenedEntries.length !== 1 ? "s" : ""}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Unique Accounts
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {uniqueAccounts}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {entryOverviews.length} submission
                        {entryOverviews.length !== 1 ? "s" : ""}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Paid Submissions
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.success.main,
                        }}
                      >
                        {paidEntries}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {unpaidEntries} unpaid
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Unpaid Submissions
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.warning.main,
                        }}
                      >
                        {unpaidEntries}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {paidEntries} paid
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Grade per Height Breakdown */}
              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 2 }}
                  gutterBottom
                >
                  Dogs per Grade per Height
                </Typography>
                <TableContainer sx={{ maxHeight: 600, overflowX: "auto" }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "background.paper",
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                          }}
                        >
                          Height
                        </TableCell>
                        {[1, 2, 3, 4, 5, 6, 7].map((grade) => (
                          <TableCell
                            key={grade}
                            align="center"
                            sx={{ fontWeight: 600, minWidth: 80 }}
                          >
                            Grade {grade}
                          </TableCell>
                        ))}
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 600,
                            bgcolor: theme.palette.primary.light + "10",
                          }}
                        >
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.keys(gradeHeightBreakdown)
                        .sort()
                        .map((height) => (
                          <TableRow key={height} hover>
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                position: "sticky",
                                left: 0,
                                bgcolor: "background.paper",
                                zIndex: 2,
                              }}
                            >
                              {height}
                            </TableCell>
                            {[1, 2, 3, 4, 5, 6, 7].map((grade) => (
                              <TableCell key={grade} align="center">
                                {gradeHeightBreakdown[height]?.[grade] || 0}
                              </TableCell>
                            ))}
                            <TableCell
                              align="center"
                              sx={{
                                fontWeight: 600,
                                bgcolor: theme.palette.primary.light + "10",
                              }}
                            >
                              {heightTotals[height] || 0}
                            </TableCell>
                          </TableRow>
                        ))}
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: theme.palette.primary.light + "10",
                            position: "sticky",
                            left: 0,
                            zIndex: 2,
                          }}
                        >
                          Total
                        </TableCell>
                        {[1, 2, 3, 4, 5, 6, 7].map((grade) => (
                          <TableCell
                            key={grade}
                            align="center"
                            sx={{
                              fontWeight: 600,
                              bgcolor: theme.palette.primary.light + "10",
                            }}
                          >
                            {gradeTotals[grade] || 0}
                          </TableCell>
                        ))}
                        <TableCell
                          align="center"
                          sx={{
                            fontWeight: 700,
                            bgcolor: theme.palette.primary.main + "20",
                            color: theme.palette.primary.dark,
                          }}
                        >
                          {totalEntries}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Class, Height, and Grade Breakdown */}
              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 2 }}
                  gutterBottom
                >
                  Dogs per Class, Height, and Grade
                </Typography>
                {Object.keys(classHeightGradeBreakdown).length === 0 ? (
                  <Alert severity="info">
                    No class data available for this show.
                  </Alert>
                ) : (
                  Object.keys(classHeightGradeBreakdown)
                    .sort()
                    .map((className) => {
                      const classData = classHeightGradeBreakdown[className];
                      const heights = Object.keys(classData).sort();
                      const allGrades = [1, 2, 3, 4, 5, 6, 7];

                      // Calculate totals for this class
                      const classHeightTotals: { [height: string]: number } =
                        {};
                      const classGradeTotals: { [grade: number]: number } = {};

                      heights.forEach((height) => {
                        let heightTotal = 0;
                        allGrades.forEach((grade) => {
                          const count = classData[height]?.[grade] || 0;
                          heightTotal += count;
                          classGradeTotals[grade] =
                            (classGradeTotals[grade] || 0) + count;
                        });
                        classHeightTotals[height] = heightTotal;
                      });

                      const classId = classNameToId[className];

                      return (
                        <Box key={className} sx={{ mb: 4 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 2,
                              flexWrap: "wrap",
                              mb: 2,
                              pb: 1,
                              borderBottom: 1,
                              borderColor: "divider",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                              }}
                            >
                              {className} (Total: {classTotals[className]})
                            </Typography>
                            {classId != null && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PictureAsPdf />}
                                disabled={loadingClassPdfId === classId}
                                onClick={() =>
                                  handleDownloadClassCompetitorsPdf(
                                    classId,
                                    className,
                                  )
                                }
                                sx={{ textTransform: "none" }}
                              >
                                {loadingClassPdfId === classId
                                  ? "Loading…"
                                  : "Class competitors PDF"}
                              </Button>
                            )}
                          </Box>
                          <TableContainer
                            sx={{ maxHeight: 400, overflowX: "auto", mb: 3 }}
                          >
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      bgcolor: "background.paper",
                                      position: "sticky",
                                      left: 0,
                                      zIndex: 3,
                                    }}
                                  >
                                    Height
                                  </TableCell>
                                  {allGrades.map((grade) => (
                                    <TableCell
                                      key={grade}
                                      align="center"
                                      sx={{ fontWeight: 600, minWidth: 80 }}
                                    >
                                      Grade {grade}
                                    </TableCell>
                                  ))}
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontWeight: 600,
                                      bgcolor:
                                        theme.palette.primary.light + "10",
                                    }}
                                  >
                                    Total
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {heights.map((height) => (
                                  <TableRow key={height} hover>
                                    <TableCell
                                      sx={{
                                        fontWeight: 500,
                                        position: "sticky",
                                        left: 0,
                                        bgcolor: "background.paper",
                                        zIndex: 2,
                                      }}
                                    >
                                      {height}
                                    </TableCell>
                                    {allGrades.map((grade) => (
                                      <TableCell key={grade} align="center">
                                        {classData[height]?.[grade] || 0}
                                      </TableCell>
                                    ))}
                                    <TableCell
                                      align="center"
                                      sx={{
                                        fontWeight: 600,
                                        bgcolor:
                                          theme.palette.primary.light + "10",
                                      }}
                                    >
                                      {classHeightTotals[height] || 0}
                                    </TableCell>
                                  </TableRow>
                                ))}
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      bgcolor:
                                        theme.palette.primary.light + "10",
                                      position: "sticky",
                                      left: 0,
                                      zIndex: 2,
                                    }}
                                  >
                                    Total
                                  </TableCell>
                                  {allGrades.map((grade) => (
                                    <TableCell
                                      key={grade}
                                      align="center"
                                      sx={{
                                        fontWeight: 600,
                                        bgcolor:
                                          theme.palette.primary.light + "10",
                                      }}
                                    >
                                      {classGradeTotals[grade] || 0}
                                    </TableCell>
                                  ))}
                                  <TableCell
                                    align="center"
                                    sx={{
                                      fontWeight: 700,
                                      bgcolor:
                                        theme.palette.primary.main + "20",
                                      color: theme.palette.primary.dark,
                                    }}
                                  >
                                    {classTotals[className]}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      );
                    })
                )}
              </Paper>
            </Box>
          )}
        </Box>
      </Fade>

      {/* Entry Detail Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="entry-detail-modal"
      >
        <Box sx={modalStyle}>
          {selectedEntry && (
            <Box sx={{ p: 4 }}>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  pb: 2,
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{ fontWeight: 600 }}
                >
                  Entry Details
                </Typography>
                <IconButton onClick={handleCloseModal} aria-label="close">
                  <Close />
                </IconButton>
              </Box>

              <Stack spacing={3}>
                {/* Reference and Payment Status */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Reference
                        </Typography>
                        <Typography variant="h6">
                          {selectedEntry.reference}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Payment Status
                        </Typography>
                        <Chip
                          label={selectedEntry.paid ? "Paid" : "Unpaid"}
                          color={selectedEntry.paid ? "success" : "warning"}
                          icon={
                            selectedEntry.paid ? <CheckCircle /> : <Warning />
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Contact Information */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Email /> Contact Information
                    </Typography>
                    <Typography variant="body1">
                      <strong>Email:</strong> {selectedEntry.email_address}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Created: {formatDate(selectedEntry.created_at)}
                    </Typography>
                  </CardContent>
                </Card>

                {/* Show Information */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <CalendarToday /> Show Information
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body1">
                        <strong>Show:</strong> {selectedEntry.payload.show_name}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Show Date:</strong>{" "}
                        {selectedEntry.payload.show_date}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Closing Date:</strong>{" "}
                        {selectedEntry.payload.closing_date}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Financial Information */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Payment /> Financial Information
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body1">
                        <strong>Show Fees:</strong>{" "}
                        {formatCurrency(selectedEntry.payload.show_fees)}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Total including Processing Fee:</strong>{" "}
                        {formatCurrency(selectedEntry.payload.total_owing)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Member: {selectedEntry.payload.is_member ? "Yes" : "No"}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Entries */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Pets /> Dog Entries
                    </Typography>
                    <Stack spacing={2}>
                      {selectedEntry.payload.entries.map((entry, index) => (
                        <Box key={index}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              backgroundColor: theme.palette.action.hover,
                            }}
                          >
                            <Stack spacing={1.5}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Pets
                                  sx={{ color: theme.palette.primary.main }}
                                />
                                <Typography variant="h6">
                                  {entry.dog_name || "Unnamed Dog"}
                                </Typography>
                              </Box>
                              <Divider />
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Handler
                                  </Typography>
                                  <Typography variant="body1">
                                    <Person
                                      sx={{
                                        fontSize: 16,
                                        mr: 0.5,
                                        verticalAlign: "middle",
                                      }}
                                    />
                                    {entry.handler_name ||
                                      `${entry.handler_first_name || ""} ${
                                        entry.handler_last_name || ""
                                      }`.trim() ||
                                      "N/A"}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Grade
                                  </Typography>
                                  <Typography variant="body1">
                                    <School
                                      sx={{
                                        fontSize: 16,
                                        mr: 0.5,
                                        verticalAlign: "middle",
                                      }}
                                    />
                                    Grade {entry.grade}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Height
                                  </Typography>
                                  <Typography variant="body1">
                                    {entry.height}
                                  </Typography>
                                </Grid>
                                {entry.kennel_club_name && (
                                  <Grid item xs={12} sm={6}>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      Kennel Club Name
                                    </Typography>
                                    <Typography variant="body1">
                                      {entry.kennel_club_name}
                                    </Typography>
                                  </Grid>
                                )}
                                {entry.class_details &&
                                  entry.class_details.length > 0 && (
                                    <Grid item xs={12}>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        gutterBottom
                                      >
                                        Classes
                                      </Typography>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                        gap={1}
                                      >
                                        {entry.class_details.map(
                                          (cd, cdIndex) => (
                                            <Chip
                                              key={cdIndex}
                                              label={cd.class_name}
                                              size="small"
                                              variant="outlined"
                                            />
                                          ),
                                        )}
                                      </Stack>
                                    </Grid>
                                  )}
                              </Grid>
                            </Stack>
                          </Paper>
                          {index < selectedEntry.payload.entries.length - 1 && (
                            <Divider sx={{ my: 2 }} />
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Box>
          )}
        </Box>
      </Modal>
    </Container>
  );
};

export default ShowEntryOverviews;
